import {VerticalSeparator} from '#/atoms/vertical-separator';
import {ActionMenuContentSectionInfoRow} from '#/components/actionmenu/subcomponents';
import {getEquidistantRangeFromMinMax} from '#/components/graphs/graphs-helpers';
import {CoinDisplayPreferences, useCoinDisplayPreferences} from '#/hooks/use-coin-display-preferences';
import {PortfolioValueGraphData} from '#/hooks/use-portfolio-value-graph-data';
import {FunctionComponent, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  ComposedChart,
  LabelList,
  LabelProps,
  Line,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import {CartesianViewBox} from 'recharts/types/util/types';

export const MarketValueMinMaxIndicatorGraph: FunctionComponent<{
  graphData: PortfolioValueGraphData[];
}> = ({graphData}) => {
  const displayPref = useCoinDisplayPreferences();

  const minAndMaxValues = useMemo(() => getMinMaxValues(graphData), [graphData]);
  const horizontalPoints = useMemo(() => getEquidistantRangeFromMinMax(minAndMaxValues), [minAndMaxValues]);
  const minPoint = graphData.findIndex(data => data.marketValue === minAndMaxValues.min);
  const maxPoint = graphData.findIndex(data => data.marketValue === minAndMaxValues.max);

  const [width, setWidth] = useState(0);

  return (
    <ResponsiveContainer
      width='100%'
      height='100%'
      onResize={val => {
        setWidth(val);
      }}
    >
      <ComposedChart data={graphData} margin={graphMargins}>
        <XAxis dataKey={'date'} scale='time' type='number' domain={['dataMin', 'dataMax']} hide={true} />

        <YAxis
          orientation='left'
          domain={[horizontalPoints[0], horizontalPoints[horizontalPoints.length - 1]]}
          ticks={horizontalPoints}
          tickMargin={10}
          interval={0}
          hide={true}
          padding={{top: 20, bottom: 20}}
        />

        <Line
          dataKey='marketValue'
          name='Market Value'
          stroke='#3182ce'
          type='monotone'
          connectNulls={true}
          yAxisId='0'
          strokeWidth={3}
          strokeLinejoin='round'
          strokeLinecap='round'
          isAnimationActive={false}
          dot={false}
        >
          <LabelList content={<CustomizedLabel parentWidth={width} min={minPoint} max={maxPoint} />} />
        </Line>

        <Tooltip content={<MarketValueGraphTooltip displayPref={displayPref} />} />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

const getMinMaxValues = (graphData: PortfolioValueGraphData[]) =>
  graphData.reduce(
    (acc, value) => {
      return {
        min: Math.min(acc.min, value.marketValue),
        max: Math.max(acc.max, value.marketValue),
      };
    },
    {min: Number.MAX_VALUE, max: 0},
  );

const graphMargins = {top: 10, right: 10, bottom: 10, left: 10};
const fontSize = 12;

type CustomizedLabelProps = LabelProps & {
  parentWidth: number;
  min: number | undefined;
  max: number | undefined;
  index?: number;
};
const CustomizedLabel: FunctionComponent<CustomizedLabelProps> = props => {
  if (
    typeof props?.index === 'number' &&
    ((props.min !== undefined && props.index === props.min) || (props.max !== undefined && props.index === props.max))
  ) {
    return (
      <CustomizedLabelContent {...props} value={props.value as number} viewBox={props.viewBox as CartesianViewBox} />
    );
  }

  return null;
};

const getDx = (bBox: DOMRect | undefined, parentWidth: number, x: number) => {
  if (!bBox) {
    return 0;
  }

  // If overflow left
  if (x < bBox.width / 2) {
    return bBox.width / 2 - x + graphMargins.left;
  }

  // If overflow right
  if (parentWidth !== undefined && x + bBox.width / 2 > parentWidth) {
    return parentWidth - (x + bBox.width / 2) - +graphMargins.right;
  }
};

const yPadding = 20;

const getDy = (bBox: DOMRect | undefined) => {
  if (!bBox) {
    return 0;
  }
  return Math.round(bBox.height / 2 - fontSize / 4);
};

const CustomizedLabelContent: FunctionComponent<
  Omit<CustomizedLabelProps, 'value' | 'viewBox'> & {
    value: number;
    viewBox: CartesianViewBox;
  }
> = ({x, y, value, parentWidth, index, min}) => {
  const displayPref = useCoinDisplayPreferences();

  const {t} = useTranslation('translation');

  const ref = useRef<SVGTextElement>(null);
  const [bBox, setBBox] = useState<DOMRect>();

  const isMin = typeof min === 'number' && min === index;

  useEffect(() => {
    if (ref.current) {
      const bBox = ref.current.getBBox();
      setBBox(bBox);
    }
  }, []);

  return (
    <text
      ref={ref}
      x={x}
      y={y}
      dx={getDx(bBox, parentWidth, x as number)}
      dy={getDy(bBox) + (isMin ? yPadding : -yPadding)}
      fill={bBox ? 'white' : 'transparent'}
      fontSize={fontSize}
      fontWeight={500}
      textAnchor='middle'
    >
      {t('components.portfolioMarketValueGraph.rangeIndicator', {
        context: isMin ? 'min' : 'max',
        currencySymbol: displayPref.symbol,
        amount: value * 1_000_000,
      })}
    </text>
  );
};

const MarketValueGraphTooltip: FunctionComponent<
  TooltipProps<number, string> & {
    displayPref: CoinDisplayPreferences;
  }
> = ({active, payload, label, displayPref}): JSX.Element => {
  const {t} = useTranslation('translation');

  if (active && payload && payload.length && displayPref) {
    return (
      <div className='text-white font-semibold rounded-xl space-y-2 bg-black p-4'>
        <div className='text-xs text-white font-semibold'>
          {t('components.portfolioMarketValueGraph.tooltip.date.value', {
            date: new Date(label),
          })}
        </div>

        <VerticalSeparator />

        <ActionMenuContentSectionInfoRow>
          {{
            left: t('components.portfolioMarketValueGraph.tooltip.marketValue.title'),
            right: (
              <span className='pl-2'>
                {t('components.portfolioMarketValueGraph.tooltip.marketValue.value', {
                  currencySymbol: displayPref.symbol,
                  amount: (payload[0]?.value ?? 0) * 1_000_000,
                })}
              </span>
            ),
          }}
        </ActionMenuContentSectionInfoRow>
      </div>
    );
  }

  return <div />;
};
